"use client";

import { Mail, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n/I18nProvider";
import { useState } from "react";
import { submitDemoRequest } from "@/lib/public-forms/api";

export default function ContactPage() {
  const { t, locale } = useI18n();
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      const fullMessage = formData.subject
        ? `[${formData.subject}] ${formData.message}`
        : formData.message;

      await submitDemoRequest({
        email: formData.email,
        fullName: formData.name || undefined,
        message: fullMessage || undefined,
        source: "contact",
        locale: locale || "ru",
      });

      setSubmitted(true);
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch {
      setSubmitError(
        t("pages.contact.form.errorFallback") ||
          "Не удалось отправить сообщение. Напишите нам на hello@keiront.com"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            {t("pages.contact.headline") || "Свяжитесь с нами"}
          </h1>
          <p className="text-xl text-slate-600">
            {t("pages.contact.subheading") ||
              "Ответим на все вопросы в течение 24 часов"}
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            {/* Email */}
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <Mail size={28} className="text-keiro-green mt-1" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">
                  {t("pages.contact.email.title") || "Email"}
                </h3>
                <a
                  href="mailto:hello@keiront.com"
                  className="text-slate-600 hover:text-keiro-green transition-colors break-all"
                >
                  hello@keiront.com
                </a>
                <p className="text-sm text-slate-500 mt-2">
                  {t("pages.contact.email.desc") ||
                    "Мы ответим быстрее по email"}
                </p>
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <Phone size={28} className="text-keiro-green mt-1" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">
                  {t("pages.contact.phone.title") || "Телефон"}
                </h3>
                <a
                  href="tel:+79384005505"
                  className="text-slate-600 hover:text-keiro-green transition-colors"
                >
                  +7 938 400-55-05
                </a>
                <p className="text-sm text-slate-500 mt-2">
                  {t("pages.contact.phone.desc") ||
                    "Пн-Пт, 10:00-18:00 (МСК)"}
                </p>
              </div>
            </div>

            {/* Offices */}
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <MapPin size={28} className="text-keiro-green mt-1" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-1">
                  {t("pages.contact.office.title") || "Офисы"}
                </h3>
                <p className="text-slate-600">Краснодар · Ереван</p>
                <p className="text-sm text-slate-500 mt-2">
                  {t("pages.contact.office.hours") ||
                    "Приёмные часы по договорённости"}
                </p>
              </div>
            </div>

            {/* Quick Links */}
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-4">
                {t("pages.contact.quickLinks.title") || "Быстрые ссылки"}
              </h3>
              <ul className="space-y-2">
                <li>
                  <a
                    href="/pricing"
                    className="text-sm text-keiro-green hover:text-keiro-green-dark transition-colors"
                  >
                    {t("pages.contact.quickLinks.pricing") || "Смотреть тарифы"}
                  </a>
                </li>
                <li>
                  <a
                    href="/about"
                    className="text-sm text-keiro-green hover:text-keiro-green-dark transition-colors"
                  >
                    {t("pages.contact.quickLinks.about") || "О компании"}
                  </a>
                </li>
                <li>
                  <a
                    href="/blog"
                    className="text-sm text-keiro-green hover:text-keiro-green-dark transition-colors"
                  >
                    {t("pages.contact.quickLinks.docs") || "Блог"}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            {submitted ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-8 text-center">
                <div className="text-4xl mb-4">✓</div>
                <h3 className="text-xl font-semibold text-emerald-800 mb-2">
                  {t("pages.contact.form.successTitle") || "Сообщение отправлено!"}
                </h3>
                <p className="text-emerald-700">
                  {t("pages.contact.form.successDesc") ||
                    "Мы ответим в течение 24 часов на указанный email."}
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 text-sm text-emerald-600 underline hover:text-emerald-800"
                >
                  {t("pages.contact.form.sendAnother") || "Отправить ещё одно сообщение"}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {submitError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-700">{submitError}</p>
                  </div>
                )}

                {/* Name */}
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-slate-900 mb-2"
                  >
                    {t("pages.contact.form.name") || "Ваше имя"}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder={
                      t("pages.contact.form.namePlaceholder") || "Иван Петров"
                    }
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-keiro-green focus:border-transparent"
                  />
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-slate-900 mb-2"
                  >
                    {t("pages.contact.form.email") || "Email"}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder={
                      t("pages.contact.form.emailPlaceholder") ||
                      "ivan@yourhotel.com"
                    }
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-keiro-green focus:border-transparent"
                  />
                </div>

                {/* Subject */}
                <div>
                  <label
                    htmlFor="subject"
                    className="block text-sm font-medium text-slate-900 mb-2"
                  >
                    {t("pages.contact.form.subjectLabel") || "Тема"}
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-keiro-green focus:border-transparent"
                  >
                    <option value="">
                      {t("pages.contact.form.selectSubject") || "Выберите тему"}
                    </option>
                    <option value="demo">
                      {t("pages.contact.form.subject.demo") || "Запросить демо"}
                    </option>
                    <option value="support">
                      {t("pages.contact.form.subject.support") || "Техподдержка"}
                    </option>
                    <option value="integration">
                      {t("pages.contact.form.subject.integration") || "Интеграция"}
                    </option>
                    <option value="sales">
                      {t("pages.contact.form.subject.sales") || "Обсудить тариф"}
                    </option>
                    <option value="other">
                      {t("pages.contact.form.subject.other") || "Другое"}
                    </option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-slate-900 mb-2"
                  >
                    {t("pages.contact.form.message") || "Сообщение"}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    placeholder={
                      t("pages.contact.form.messagePlaceholder") ||
                      "Расскажите нам о вашей задаче..."
                    }
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-keiro-green focus:border-transparent resize-none"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="default"
                  className="w-full h-11 font-semibold"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? t("pages.contact.form.sending") || "Отправляем..."
                    : t("pages.contact.form.send") || "Отправить"}
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  {t("pages.contact.form.privacy") ||
                    "Мы не делимся вашими данными с третьими лицами"}
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
